import { useState, useEffect } from 'react';
import { backdropClose } from '../utils/backdropClose';
import { X, CreditCard, Loader2, Calendar, MessageSquare, IndianRupee } from 'lucide-react';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import toast from 'react-hot-toast';
import type { GymPurchase } from './ViewGymPurchaseModal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  purchase: GymPurchase | null;
  initialTab?: 'payment' | 'details';
}

const PAYMENT_METHODS = ['Cash', 'UPI', 'Card', 'Bank Transfer', 'Cheque', 'Other'];

export default function EditGymPurchaseModal({ isOpen, onClose, onSuccess, purchase, initialTab = 'payment' }: Props) {
  const [saving, setSaving] = useState(false);

  // Payment fields
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('');
  const [payDate, setPayDate] = useState(new Date().toISOString().split('T')[0]);
  const [payNotes, setPayNotes] = useState('');

  // Purchase fields (Meta)
  const [dueDate, setDueDate] = useState('');

  useEffect(() => {
    if (isOpen && purchase) {
      const pendingStr = Number(purchase.pendingAmount) > 0 ? purchase.pendingAmount.toString() : '';
      setPayAmount(pendingStr);
      setPayMethod('');
      setPayDate(new Date().toISOString().split('T')[0]);
      setPayNotes(purchase.notes || '');
      setDueDate(purchase.dueDate ? new Date(purchase.dueDate).toISOString().split('T')[0] : '');
    }
  }, [isOpen, purchase]);

  if (!isOpen || !purchase) return null;

  const pending = Number(purchase.pendingAmount) || 0;
  const memberName = purchase.member.displayName || purchase.member.fullName || 'Member';

  const handleRecordPayment = async () => {
    const amt = parseFloat(payAmount);
    // If user entered amount, record it. Otherwise just save the meta?
    // User goal is usually "Record Receipt" here.
    
    if (amt > 0) {
      if (amt > pending + 0.01) return toast.error(`Amount exceeds pending balance of ₹${pending.toFixed(2)}`);
      if (!payMethod) return toast.error('Select a payment method');
    }

    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const headers = { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' };

      // 1. Update purchase meta (Due Date / Notes)
      const updateRes = await fetch(`${API_BASE_URL}/api/gym-purchases/${purchase.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ notes: payNotes.trim() || null, dueDate: dueDate || null })
      });
      if (!updateRes.ok) throw new Error('Failed to update purchase details');

      // 2. Record payment if amount > 0
      if (amt > 0) {
        const payRes = await fetch(`${API_BASE_URL}/api/gym-purchases/${purchase.id}/payments`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ amount: amt, paymentMethod: payMethod, paymentDate: payDate, notes: payNotes.trim() || null })
        });
        const data = await payRes.json();
        if (!payRes.ok || !data.success) throw new Error(data.error || 'Failed to record payment');
      }

      toast.success(amt > 0 ? 'Receipt authorized and details saved!' : 'Purchase details updated');
      onSuccess();
      onClose();
    } catch (err: any) { 
        toast.error(err.message || 'Action failed'); 
    } finally { 
        setSaving(false); 
    }
  };

    const paying = parseFloat(payAmount) > 0;

    return (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[1002] flex items-center justify-center p-4 animate-in fade-in duration-300" {...backdropClose(onClose)}>
            <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-md flex flex-col overflow-hidden animate-in zoom-in-95 duration-500" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="bg-gradient-to-br from-slate-900 to-indigo-900 px-6 py-6 text-white shrink-0 relative overflow-hidden">
                    <div className="relative z-10 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20 flex items-center justify-center shadow-inner group-hover:scale-110 transition-transform">
                                <CreditCard className="w-6 h-6 text-indigo-200" />
                            </div>
                            <div className="min-w-0">
                                <h2 className="text-lg font-black tracking-tight leading-none">
                                    {initialTab === 'payment' ? 'Record Receipt' : 'Modify Order Meta'}
                                </h2>
                                <p className="text-indigo-300/60 text-[10px] font-black uppercase tracking-[0.2em] mt-2 truncate">
                                    {purchase.invoiceId || purchase.id.slice(0, 8)} · {memberName}
                                </p>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-2.5 bg-white/10 hover:bg-rose-500 rounded-2xl border border-white/10 transition-all group active:scale-90 text-white">
                            <X className="w-5 h-5 text-white/60 group-hover:text-white" />
                        </button>
                    </div>
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                </div>

                <div className="p-8 space-y-6 overflow-y-auto max-h-[80vh] custom-scrollbar text-slate-800">
                    {/* Summary Card */}
                    <div className="bg-slate-900 rounded-[2rem] p-6 text-white shadow-xl shadow-slate-200">
                        <p className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em] mb-3">Recoverable Debt</p>
                        <div className="flex items-end justify-between">
                            <p className="text-3xl font-black tracking-tight">₹{pending.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>
                            <p className="text-[9px] font-black text-white/30 uppercase tracking-widest pb-1 opacity-70">of ₹{Number(purchase.totalAmount).toLocaleString()} Total Value</p>
                        </div>
                    </div>

                    {/* Payment Form */}
                    {pending > 0 && initialTab === 'payment' && (
                        <div className="space-y-6 animate-in slide-in-from-top-4 duration-300">
                            <div>
                                <label className="lbl">Installment Value (₹)</label>
                                <div className="relative group">
                                    <IndianRupee className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                                    <input type="number" min={0.01} max={pending + 0.01} step={0.01} value={payAmount}
                                        onChange={e => setPayAmount(e.target.value)} placeholder={`Max ₹${pending.toFixed(2)}`}
                                        className="fi !pl-14 h-14" autoFocus />
                                </div>
                            </div>

                            <div>
                                <label className="lbl">Gateway / Payment Mode</label>
                                <div className="grid grid-cols-3 gap-2.5 mt-2">
                                    {PAYMENT_METHODS.map(m => (
                                        <button key={m} type="button" onClick={() => setPayMethod(m)}
                                            className={`py-3.5 rounded-2xl text-[9px] font-black uppercase tracking-widest border transition-all ${payMethod === m ? 'bg-indigo-600 text-white border-indigo-500 shadow-lg shadow-indigo-600/20 active:scale-95' : 'bg-white text-slate-500 border-slate-100 hover:border-indigo-100'}`}>
                                            {m}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="lbl">Payment Date</label>
                                <div className="relative">
                                    <Calendar className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <input type="date" value={payDate} onChange={e => setPayDate(e.target.value)} className="fi !pl-14 h-14" />
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="space-y-6">
                        <div>
                            <label className="lbl">Due Date (Optional)</label>
                            <div className="relative">
                                <Calendar className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 text-amber-500" />
                                <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="fi !pl-14 h-14" min={new Date().toISOString().split('T')[0]} />
                            </div>
                        </div>

                        <div>
                            <label className="lbl">Note (Optional)</label>
                            <div className="relative group">
                                <MessageSquare className="absolute left-5 top-5 w-4 h-4 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                                <textarea value={payNotes} onChange={e => setPayNotes(e.target.value)} rows={3}
                                    placeholder="e.g. Bank Ref ID or order remarks"
                                    className="fi !pl-14 pt-4 resize-none rounded-[1.5rem] min-h-[100px]"
                                />
                            </div>
                        </div>
                    </div>

                    <button onClick={handleRecordPayment} disabled={saving}
                        className="w-full py-5 rounded-[2rem] bg-indigo-600 text-white text-[11px] font-black uppercase tracking-[0.2em] hover:bg-slate-900 transition-all shadow-2xl shadow-indigo-600/20 disabled:opacity-50 flex items-center justify-center gap-3 ring-4 ring-indigo-500/5 group active:scale-[0.98]">
                        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
                        {saving ? 'Processing...' : (paying ? 'Authorize Receipt' : 'Save Changes')}
                    </button>
                </div>

        {/* Footer info */}
        <div className="p-6 bg-slate-50 border-t border-slate-100 text-center">
           <p className="text-[8px] font-black text-slate-300 uppercase tracking-[0.3em]">Managed via Terminal 1.0.4 • Audit Encrypted</p>
        </div>

        <style>{`
          .lbl { display:block; font-size:9px; font-weight:900; text-transform:uppercase; letter-spacing:.2em; color:rgb(148 163 184); margin-bottom:10px; padding-left:4px; }
          .fi { width:100%; padding:14px 20px; border-radius:1.5rem; border:2px solid rgb(241 245 249); font-size:14px; font-weight:600; outline:none; transition:all .3s ease; background:rgb(248 250 252); color:rgb(15 23 42); box-sizing: border-box; }
          .fi:focus { border-color:rgb(99 102 241); box-shadow:0 0 0 10px rgb(99 102 241/0.05); background:white; }
          
          /* Hide default calendar icon but keep input clickable */
          input[type="date"]::-webkit-calendar-picker-indicator {
            background: transparent;
            bottom: 0;
            color: transparent;
            cursor: pointer;
            height: auto;
            left: 0;
            position: absolute;
            right: 0;
            top: 0;
            width: auto;
          }

          .custom-scrollbar::-webkit-scrollbar { width: 4px; }
          .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
          .custom-scrollbar::-webkit-scrollbar-thumb { background: rgb(226 232 240); border-radius: 10px; }
        `}</style>
      </div>
    </div>
  );
}


